<template>
  <div>
    <!-- <load-profile /> -->
    <b-card id="container" ref="container">
      <b-card-text class="mb-2">
        <h5>
          {{ $t("general.customizeCallWidget") }}
        </h5>
      </b-card-text>
      <validation-observer ref="editForm" #default="{ invalid }">
        <n-form-confirmation
          key="editForm"
          :form="$refs.editForm"
          :disabled="invalid"
          @submit="validateForm"
        >
          <b-row>
            <b-col md="6">
              <!-- left side -->
              <n-input v-model="data" :fields="fields" :init-value="initData">
                <template #companyLogo="item">
                  <b-form-group
                    v-if="item.field.type !== 'divider'"
                    :label-for="item.field.key"
                    :description="getDesciption(item.field)"
                    :class="item.field.class"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :vid="item.field.key"
                      :name="item.field.label"
                      :rules="item.field.rules"
                    >
                      <label class="d-block">
                        {{ $t(item.field.label) }}
                        <span class="text-muted">
                          - {{ $t("field.optional") }}
                        </span>
                      </label>
                      <n-single-image-uploader
                        :ref="item.field.key"
                        v-model="data.file.companyLogo"
                        :pw="item.field.pw"
                        :ph="item.field.ph"
                        :full-width="item.field.fullWidth"
                        :image="data.companyLogoUrl"
                        :readonly="item.field.disabled"
                        :class="errors.length > 0 ? 'is-invalid' : null"
                        @change="onImageChange"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </template>
              </n-input>
              <label class="d-block">
                <span>{{ $t("label.incomingCallStatus") }}</span>
              </label>
              <b-form-checkbox v-model="isEnabled" name="summary-toggle" switch>
                <p class="font-weight-bold">
                  {{
                    $t(`pages.chatSettings.ticketEditor.enable.${isEnabled}`)
                  }}
                </p>
              </b-form-checkbox>
            </b-col>
            <b-col md="6">
              <!-- right side -->
              <div
                class="chat-widget-container call"
                :class="stickyClass"
                :style="{ maxWidth: data.size == 'm' ? `360px` : `330px` }"
              >
                <div class="chat-widget-header" />
                <div class="chat-widget-body">
                  <span v-if="data.companyLogo" class="logo rounded-circle">
                    <img
                      :key="`img-${data.companyLogoUrl}`"
                      :src="data.companyLogoUrl"
                      style="
                        height: 100%;
                        width: 100%;
                        border-radius: 50%;
                        object-fit: cover;
                      "
                    />
                  </span>
                  <div v-else class="logo rounded-circle">
                    {{ $t("field.logo") }}
                  </div>
                  <p class="call-center">
                    {{ $t("breadcrumb.callCenter") }}
                  </p>
                  <div class="btn-actions">
                    <div class="btn-container">
                      <div class="btn-action">
                        <MuteDemoIcon
                          :width="25"
                          :height="25"
                          :fill="data.primaryColor"
                        />
                      </div>
                      <p>{{ $t("pages.callCenter.callActions.mute") }}</p>
                    </div>
                    <div class="btn-container">
                      <div class="btn-action">
                        <KeypadIcon
                          :width="25"
                          :height="25"
                          :fill="data.primaryColor"
                        />
                      </div>
                      <p>{{ $t("pages.callCenter.callActions.keypad") }}</p>
                    </div>
                    <div class="btn-container">
                      <div class="btn-action">
                        <SpeakerIcon
                          :width="25"
                          :height="25"
                          :fill="data.primaryColor"
                        />
                      </div>
                      <p>{{ $t("pages.callCenter.callActions.speakerOn") }}</p>
                    </div>
                  </div>
                  <div class="note-container">
                    <div class="note">
                      <NoteIcon class="icon" />
                      <p>
                        {{ $t("pages.callCenter.note") }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="chat-widget-footer">
                  <div class="call">
                    <div class="call-container">
                      <CallDemoIcon :width="25" :height="25" />
                    </div>
                    <p class="end-call">
                      {{ $t("pages.callCenter.callActions.endCall") }}
                    </p>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="text-right">
              <n-button-loading
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                class="mt-1"
                :loading="loading"
                :disabled="invalid"
              >
                {{ $t("button.saveChanges") }}
              </n-button-loading>
            </b-col>
          </b-row>
        </n-form-confirmation>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BLink,
  BAvatar,
  BInputGroup,
  BInputGroupAppend,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import NButtonLoading from "@/components/NButtonLoading";
import NFormConfirmation from "@/components/NFormConfirmation";
import NInput from "@/components/NInput";
import NSingleImageUploader from "@/components/NSingleImageUploader";
import {
  generateTextColor,
  generateVariantColor,
  hexToRgbA,
} from "@/libs/helper";
import useJwt from "@/auth/jwt/useJwt";
import Repository from "@/repositories/RepositoryFactory";
import NoteIcon from "@/components/icons/NoteIcon.vue";
import SpeakerIcon from "@/components/icons/SpeakerIcon.vue";
import MuteDemoIcon from "@/components/icons/MuteDemoIcon.vue";
import KeypadIcon from "@/components/icons/KeypadIcon.vue";
import CallDemoIcon from "@/components/icons/CallDemoIcon.vue";
import FormInput from "./formInput";

const CallCenterRegistry = Repository.get("callCenter");
const FileRepository = Repository.get("file");

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BLink,
    BAvatar,
    BInputGroup,
    BInputGroupAppend,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    NButtonLoading,
    NFormConfirmation,
    NInput,
    NSingleImageUploader,

    NoteIcon,
    MuteDemoIcon,
    KeypadIcon,
    SpeakerIcon,
    CallDemoIcon,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: {
        file: { companyLogo: null, waitingSound: null },
        companyLogo: "",
        waitingSound: "",
        primaryColor: "",
        timeout: "",
        waitingSoundUrl: "",
      },
      borderColor: null,
      initData: {},
      file: null,
      loading: false,
      isSticky: false,
      stickyClass: "",
      chatPosTop: 0,
      text: "",
      isEnabled: false,

      generateTextColor,
      generateVariantColor,
      hexToRgbA,
    };
  },
  computed: {
    userData() {
      return this.$store.state.profile?.email || this.$store.state.profile?.phone
        ? this.$store.state.profile
        : JSON.parse(
            localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName)
          );
    },
  },
  mounted() {
    this.show();
    const chat = document.getElementById("container");
    this.chatPosTop = chat.getBoundingClientRect().top;
    window.addEventListener("scroll", this.handleScroll);
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    setColor() {
      const color = this.generateVariantColor(this.data.color);
      return color == "light" ? "#fff" : "#4b4b4b";
    },
    getDesciption(field) {
      const image = ["single-image", "async-single-image"];
      if (image.includes(field.type)) {
        return this.$t("general.recommendDimension", {
          ph: field.ph,
          pw: field.pw,
        });
      }
      if (field.hint) {
        return field.hint;
      }
      return "";
    },
    handleScroll(event) {
      const scrollPos = window.scrollY;
      if (scrollPos > this.chatPosTop) {
        this.stickyClass = "sticky";
      } else {
        this.stickyClass = "";
      }
    },
    onImageChange(file) {
      this.data.companyLogoUrl = file;
    },
    onImageBotChange(file) {
      this.data.botLogo = file;
    },
    show() {
      this.loading = true;
      CallCenterRegistry.widget
        .show()
        .then((response) => {
          const data = response?.data?.data;

          if (data) {
            this.initData = {
              ...data,
              ...(!Object.keys(data.waitingSound)?.length && {
                waitingSound: null,
              }),
              companyLogoUrl: data.companyLogo.url,
              waitingSoundUrl: data.waitingSound?.url,
            };
            this.isEnabled = data.isEnabled;
          }
        })
        .catch()
        .then(() => {
          this.loading = false;
        });
    },
    validateForm() {
      this.$refs.editForm.validate().then(async (success) => {
        if (success) {
          if (this.data.file.companyLogo) {
            try {
              const response = await this.uploadImage(
                this.data.file.companyLogo
              );
              this.data.companyLogo = response?.data?.data;
            } catch (error) {
              this.loading = false;
            }
          }

          if (this.data.waitingSound instanceof File) {
            try {
              const response = await this.uploadImage(this.data.waitingSound);
              this.data.waitingSound = response?.data?.data;
            } catch (error) {
              this.loading = false;
            }
          }

          this.submit();
        }
      });
    },
    async uploadImage(fileLogo) {
      this.loading = true;
      const formData = new FormData();
      formData.append("image", fileLogo);
      return FileRepository.upload(formData);
    },
    submit() {
      this.loading = true;
      CallCenterRegistry.widget
        .update({
          companyLogo: {
            mediaId: this.data.companyLogo.mediaId,
            fileType: this.data.companyLogo.fileType,
            url: this.data.companyLogo.url,
          },
          waitingSound: {
            mediaId: this.data.waitingSound?.mediaId,
            fileType: this.data.waitingSound?.fileType,
            url: this.data.waitingSound?.url,
          },
          primaryColor: this.data.primaryColor,
          timeout: this.data.timeout,
          isEnabled: this.isEnabled,
        })
        .then((response) => {})
        .catch(() => {
          this.loading = false;
        })
        .then(() => {
          this.loading = false;
        });
    },
    back() {
      this.$router.back();
    },
  },
  setup() {
    const fields = FormInput;
    const permissionType = "customize-call-widget";

    return { fields, permissionType };
  },
};
</script>

<style scoped lang="scss">
.call {
  .chat-widget-header,
  .chat-widget-body,
  .chat-widget-footer {
    background-color: #f0f0f0 !important;
  }

  .chat-widget-header {
    padding: 0 !important;
    height: 50px !important;
  }

  .chat-widget-body {
    display: flex;
    align-items: center;
    flex-direction: column;

    .logo {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 6rem;
      height: 6rem;
      background-color: #e4e4e4;
      margin-bottom: 1rem;
      flex-shrink: 0;
    }

    .call-center {
      margin-bottom: 4.5rem;
      font-weight: bold;
    }

    .note-container {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }

    .note {
      box-shadow: 0px 0px 1.5px 0px #00000040;
      font-size: 10px;
      line-height: 20px;
      letter-spacing: 0px;
      text-align: left;
      border-radius: 1rem;
      padding: 0.75rem;
      display: flex;
      justify-content: start;
      margin-top: 1rem;

      .icon {
        flex-shrink: 0;
        margin-top: 0.125rem;
        margin-right: 0.25rem;
      }

      p {
        margin: 0;
      }
    }

    .btn-actions {
      width: 100%;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      display: flex;
      gap: 2rem;
      justify-content: center;
      align-items: center;

      .btn-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;

        p {
          font-size: 12px;
        }

        .btn-action {
          border-radius: 50%;
          height: 4rem;
          width: 4rem;
          display: flex;
          justify-content: center;
          align-items: center;
          box-shadow: 0px 0px 1.5px 0px #00000040;
        }
      }
    }
  }

  .chat-widget-footer {
    display: flex;
    justify-content: center;
    align-items: center;

    .call {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
    }

    .end-call {
      font-size: 12px;
    }

    .call-container {
      background: #cd2525;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 4.25rem;
      height: 4.25rem;
      border-radius: 50%;
      margin-top: 1.5rem;
    }

    p {
      background-color: transparent;
      text-align: center;
      margin-top: 0.5rem;
      margin-bottom: 0;
    }
  }
}
</style>
